import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { notifications, type NotificationItem } from '@/mocks/notifications';

const typeIcons: Record<string, string> = {
  appointment: 'ri-calendar-check-line',
  payment: 'ri-money-cny-circle-line',
  system: 'ri-information-line',
  package: 'ri-gift-line',
  warning: 'ri-error-warning-line',
  staff: 'ri-user-line',
};

const typeColors: Record<string, string> = {
  appointment: '#3B82F6',
  payment: '#10B981',
  system: '#8B5CF6',
  package: '#C9A962',
  warning: '#EF4444',
  staff: '#2D5F3E',
};

export default function TopBar() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifList, setNotifList] = useState<NotificationItem[]>(notifications);
  const notifRef = useRef<HTMLDivElement>(null);

  const today = new Date().toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  });

  const unreadCount = notifList.filter((n) => !n.read).length;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    }
    if (notifOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const markRead = (id: number) => {
    setNotifList((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllRead = () => {
    setNotifList((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleNotifClick = (notif: NotificationItem) => {
    markRead(notif.id);
    setNotifOpen(false);
    if (notif.link) navigate(notif.link);
  };

  return (
    <header
      className="fixed top-0 right-0 z-30 h-16 flex items-center justify-between px-4 md:px-6 transition-all duration-300"
      style={{
        left: '16rem',
        background: 'var(--topbar-bg)',
        borderBottom: '1px solid var(--border-color)',
      }}
    >
      {/* Left: date + quick add */}
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium hidden md:block" style={{ color: 'var(--text-secondary)' }}>
          {today}
        </span>
        <button
          onClick={() => navigate('/randevular')}
          className="spa-btn-primary text-xs px-3 py-2 rounded-spa-sm flex items-center gap-2"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-sm" />
          </div>
          <span className="hidden sm:inline">Hızlı Randevu</span>
          <span className="sm:hidden">Randevu</span>
        </button>
      </div>

      {/* Right: theme, notifications, profile */}
      <div className="flex items-center gap-2 md:gap-3">
        <button
          onClick={toggleTheme}
          className="w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer"
          style={{ background: 'var(--bg-tertiary)' }}
          title={theme === 'light' ? 'Karanlık Tema' : 'Aydınlık Tema'}
        >
          <i className={theme === 'light' ? 'ri-moon-line' : 'ri-sun-line'} style={{ color: 'var(--text-secondary)' }} />
        </button>

        {/* Notification Dropdown */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-full transition-all relative cursor-pointer"
            style={{ background: 'var(--bg-tertiary)' }}
          >
            <i className="ri-notification-3-line" style={{ color: 'var(--text-secondary)' }} />
            {unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1"
                style={{ background: '#EF4444' }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div
              className="absolute right-0 top-12 w-[360px] rounded-xl shadow-lg overflow-hidden"
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                zIndex: 50,
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Bildirimler
                </h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs cursor-pointer hover:underline"
                    style={{ color: 'var(--gold)' }}
                  >
                    Tümünü Okundu İşaretle
                  </button>
                )}
              </div>

              {/* List */}
              <div className="max-h-[400px] overflow-y-auto">
                {notifList.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                      <i className="ri-notification-off-line text-lg" style={{ color: 'var(--text-secondary)' }} />
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Henüz bildirim yok</p>
                  </div>
                ) : (
                  notifList.map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => handleNotifClick(notif)}
                      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-black/5 transition-colors cursor-pointer"
                      style={{
                        background: notif.read ? 'transparent' : 'rgba(201,169,98,0.06)',
                        borderBottom: '1px solid var(--border-color)',
                      }}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: typeColors[notif.type] + '15' }}
                      >
                        <i className={`${typeIcons[notif.type]} text-sm`} style={{ color: typeColors[notif.type] }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                            {notif.title}
                          </p>
                          {notif.count !== undefined && (
                            <span
                              className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                              style={{ background: typeColors[notif.type] + '15', color: typeColors[notif.type] }}
                            >
                              {notif.count}
                            </span>
                          )}
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: 'var(--gold)' }} />
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {notif.description}
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                          {notif.timestamp}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full cursor-pointer"
          style={{ background: 'var(--bg-tertiary)' }}
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
            A
          </div>
          <span className="text-sm font-medium hidden md:block" style={{ color: 'var(--text-primary)' }}>
            Admin
          </span>
        </div>
      </div>
    </header>
  );
}